/* eslint-disable func-style */
import React from "react";

/**
 * Container Block Component Configuration
 * Modular component that can be used in both puckBundle.js and puckHeaderBundle.js
 *
 * @param {Function} ColorPickerField - The ColorPickerField component to use for color selection
 * @param {Function} MediaField - The MediaField component to use for image selection
 * @param {Function} VideoField - The VideoField component to use for video selection
 * @param {Function} getEditorData - Function to get current editor data (getPuckEditorData or getPuckHeaderEditorData)
 * @returns {Object} Container configuration object for Puck
 */
export function createContainerBlock(
  ColorPickerField,
  MediaField,
  VideoField,
  getEditorData = () => {
    // Default: try both
    if (window.getPuckEditorData) {
      return window.getPuckEditorData();
    }
    if (window.getPuckHeaderEditorData) {
      return window.getPuckHeaderEditorData();
    }
    return null;
  }
) {
  return {
    label: "Container",
    fields: {
      containerWidth: {
        type: "select",
        label: "Container Width",
        options: [
          { label: "Standard Container", value: "container" },
          { label: "Full Width Fluid", value: "fluid" },
          { label: "Edge-to-Edge Section", value: "fullbleed" }
        ]
      },
      padding: {
        type: "select",
        label: "Padding",
        options: [
          { label: "None (0px)", value: "0" },
          { label: "Small (8px)", value: "8" },
          { label: "Medium (16px)", value: "16" },
          { label: "Large (24px)", value: "24" },
          { label: "Extra Large (32px)", value: "32" },
          { label: "XXL (48px)", value: "48" }
        ]
      },
      shadow: {
        type: "radio",
        label: "Shadow",
        options: [
          { label: "On", value: true },
          { label: "Off", value: false }
        ]
      },
      backgroundType: {
        type: "select",
        label: "Background Type",
        options: [
          { label: "None", value: "none" },
          { label: "Solid Color", value: "color" },
          { label: "Gradient", value: "gradient" },
          { label: "Image", value: "image" },
          { label: "Video", value: "video" }
        ]
      },
      backgroundColor: {
        type: "custom",
        label: "Background Color",
        render: ({ field, value, onChange, ...props }) => {
          const getBackgroundType = () => {
            const data = getEditorData();
            if (data && data.content) {
              const containerComponent = data.content.find(
                (c) => c.type === "Container"
              );
              if (
                containerComponent &&
                containerComponent.props &&
                containerComponent.props.backgroundType
              ) {
                return containerComponent.props.backgroundType;
              }
            }
            return "none";
          };

          const [backgroundType, setBackgroundType] = React.useState(
            getBackgroundType()
          );

          React.useEffect(() => {
            const checkBackgroundType = () => {
              const currentType = getBackgroundType();
              if (currentType !== backgroundType) {
                setBackgroundType(currentType);
              }
            };

            checkBackgroundType();
            document.addEventListener("puck:dataChange", checkBackgroundType);
            return () => {
              document.removeEventListener(
                "puck:dataChange",
                checkBackgroundType
              );
            };
          }, [backgroundType]);

          if (backgroundType !== "color") {
            return null;
          }

          return React.createElement(ColorPickerField, {
            field,
            value,
            onChange,
            ...props,
            showAlpha: true
          });
        }
      },
      gradient: {
        type: "custom",
        label: "Gradient",
        render: ({ field, value, onChange }) => {
          const getBackgroundType = () => {
            const data = getEditorData();
            if (data && data.content) {
              // Search recursively through content and zones
              function findContainer(components) {
                if (!components || !Array.isArray(components)) {
                  return null;
                }
                for (const component of components) {
                  if (component.type === "Container" && component.props) {
                    return component;
                  }
                  // Check zones recursively
                  if (component.props && component.props.zones) {
                    for (const zoneKey in component.props.zones) {
                      const zoneComponents = component.props.zones[zoneKey];
                      const found = findContainer(zoneComponents);
                      if (found) return found;
                    }
                  }
                }
                return null;
              }
              const containerComponent = findContainer(data.content);
              if (
                containerComponent &&
                containerComponent.props &&
                containerComponent.props.backgroundType
              ) {
                return containerComponent.props.backgroundType;
              }
            }
            return "none";
          };

          const [backgroundType, setBackgroundType] = React.useState(
            getBackgroundType()
          );

          React.useEffect(() => {
            const checkBackgroundType = () => {
              const currentType = getBackgroundType();
              if (currentType !== backgroundType) {
                setBackgroundType(currentType);
              }
            };

            checkBackgroundType();
            document.addEventListener("puck:dataChange", checkBackgroundType);
            return () => {
              document.removeEventListener(
                "puck:dataChange",
                checkBackgroundType
              );
            };
          }, [backgroundType]);

          if (backgroundType !== "gradient") {
            return null;
          }

          const gradientValue = value || {
            enabled: false,
            color: "rgba(0, 0, 0, 0.5)",
            type: "linear",
            direction: "to bottom"
          };

          return React.createElement(
            "div",
            { className: "puck-field-group" },
            React.createElement(
              "label",
              { className: "puck-field-label" },
              field.label || "Gradient"
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Enable Gradient"
              ),
              React.createElement(
                "div",
                { className: "btn-group", role: "group" },
                React.createElement(
                  "button",
                  {
                    type: "button",
                    className: `btn btn-sm ${
                      gradientValue.enabled !== false
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`,
                    "data-val": "false",
                    onClick: () => onChange({ ...gradientValue, enabled: true })
                  },
                  "On"
                ),
                React.createElement(
                  "button",
                  {
                    type: "button",
                    className: `btn btn-sm ${
                      gradientValue.enabled === false
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`,
                    "data-val": "false",
                    onClick: () =>
                      onChange({ ...gradientValue, enabled: false })
                  },
                  "Off"
                )
              )
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(ColorPickerField, {
                field: { label: "Gradient Color" },
                value: gradientValue.color || "rgba(0, 0, 0, 0.5)",
                onChange: (color) => onChange({ ...gradientValue, color }),
                showAlpha: true
              })
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Gradient Type"
              ),
              React.createElement(
                "select",
                {
                  className: "form-control",
                  value: gradientValue.type || "linear",
                  "data-val": "false",
                  onChange: (e) =>
                    onChange({ ...gradientValue, type: e.target.value })
                },
                React.createElement("option", { value: "linear" }, "Linear"),
                React.createElement("option", { value: "radial" }, "Radial")
              )
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Direction"
              ),
              React.createElement(
                "select",
                {
                  className: "form-control",
                  value: gradientValue.direction || "to bottom",
                  "data-val": "false",
                  onChange: (e) =>
                    onChange({ ...gradientValue, direction: e.target.value })
                },
                React.createElement(
                  "option",
                  { value: "to right" },
                  "To Right"
                ),
                React.createElement(
                  "option",
                  { value: "to bottom" },
                  "To Bottom"
                ),
                React.createElement("option", { value: "to left" }, "To Left"),
                React.createElement("option", { value: "to top" }, "To Top"),
                React.createElement(
                  "option",
                  { value: "to bottom right" },
                  "To Bottom Right"
                ),
                React.createElement(
                  "option",
                  { value: "to bottom left" },
                  "To Bottom Left"
                ),
                React.createElement(
                  "option",
                  { value: "to top right" },
                  "To Top Right"
                ),
                React.createElement(
                  "option",
                  { value: "to top left" },
                  "To Top Left"
                )
              )
            )
          );
        }
      },
      image: {
        type: "custom",
        label: "Image",
        render: ({ field, value, onChange }) => {
          const getBackgroundType = () => {
            const data = getEditorData();
            if (data && data.content) {
              // Search recursively through content and zones
              function findContainer(components) {
                if (!components || !Array.isArray(components)) {
                  return null;
                }
                for (const component of components) {
                  if (component.type === "Container" && component.props) {
                    return component;
                  }
                  // Check zones recursively
                  if (component.props && component.props.zones) {
                    for (const zoneKey in component.props.zones) {
                      const zoneComponents = component.props.zones[zoneKey];
                      const found = findContainer(zoneComponents);
                      if (found) return found;
                    }
                  }
                }
                return null;
              }
              const containerComponent = findContainer(data.content);
              if (
                containerComponent &&
                containerComponent.props &&
                containerComponent.props.backgroundType
              ) {
                return containerComponent.props.backgroundType;
              }
            }
            return "none";
          };

          const [backgroundType, setBackgroundType] = React.useState(
            getBackgroundType()
          );

          React.useEffect(() => {
            const checkBackgroundType = () => {
              const currentType = getBackgroundType();
              if (currentType !== backgroundType) {
                setBackgroundType(currentType);
              }
            };

            checkBackgroundType();
            document.addEventListener("puck:dataChange", checkBackgroundType);
            return () => {
              document.removeEventListener(
                "puck:dataChange",
                checkBackgroundType
              );
            };
          }, [backgroundType]);

          if (backgroundType !== "image") {
            return null;
          }

          return React.createElement(
            "div",
            { className: "puck-field-group" },
            React.createElement(
              "label",
              { className: "puck-field-label" },
              field.label || "Image"
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "URL"
              ),
              React.createElement(MediaField, {
                field: { label: "URL" },
                value: value?.url || "",
                onChange: (url) => onChange({ ...value, url })
              })
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Mode"
              ),
              React.createElement(
                "div",
                { className: "btn-group", role: "group" },
                ["inline", "bg", "custom"].map((mode) =>
                  React.createElement(
                    "button",
                    {
                      key: mode,
                      type: "button",
                      className: `btn btn-sm ${
                        (value?.mode || "bg") === mode
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, mode })
                    },
                    mode === "inline"
                      ? "Inline"
                      : mode === "bg"
                      ? "Background"
                      : "Custom"
                  )
                )
              )
            )
          );
        }
      },
      video: {
        type: "custom",
        label: "Background Video",
        render: ({ field, value, onChange }) => {
          const getBackgroundType = () => {
            const data = getEditorData();
            if (data && data.content) {
              // Search recursively through content and zones
              function findContainer(components) {
                if (!components || !Array.isArray(components)) {
                  return null;
                }
                for (const component of components) {
                  if (component.type === "Container" && component.props) {
                    return component;
                  }
                  // Check zones recursively
                  if (component.props && component.props.zones) {
                    for (const zoneKey in component.props.zones) {
                      const zoneComponents = component.props.zones[zoneKey];
                      const found = findContainer(zoneComponents);
                      if (found) return found;
                    }
                  }
                }
                return null;
              }
              const containerComponent = findContainer(data.content);
              if (
                containerComponent &&
                containerComponent.props &&
                containerComponent.props.backgroundType
              ) {
                return containerComponent.props.backgroundType;
              }
            }
            return "none";
          };

          const [backgroundType, setBackgroundType] = React.useState(
            getBackgroundType()
          );

          React.useEffect(() => {
            const checkBackgroundType = () => {
              const currentType = getBackgroundType();
              if (currentType !== backgroundType) {
                setBackgroundType(currentType);
              }
            };

            checkBackgroundType();
            document.addEventListener("puck:dataChange", checkBackgroundType);
            return () => {
              document.removeEventListener(
                "puck:dataChange",
                checkBackgroundType
              );
            };
          }, [backgroundType]);

          if (backgroundType !== "video") {
            return null;
          }

          return React.createElement(
            "div",
            { className: "puck-field-group" },
            React.createElement(
              "label",
              { className: "puck-field-label" },
              field.label || "Background Video"
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Video URL"
              ),
              React.createElement(VideoField, {
                field: { label: "Video URL" },
                value: value?.url || "",
                onChange: (url) => onChange({ ...value, url })
              })
            ),
            React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                "Poster Image (fallback/thumbnail)"
              ),
              React.createElement(MediaField, {
                field: { label: "Poster Image" },
                value: value?.poster || "",
                onChange: (poster) => onChange({ ...value, poster })
              })
            )
          );
        }
      }
    },
    defaultProps: {
      containerWidth: "fluid",
      padding: "24",
      shadow: true,
      backgroundType: "none",
      backgroundColor: "#ffffff",
      gradient: {
        enabled: false,
        color: "rgba(0, 0, 0, 0.5)",
        type: "linear",
        direction: "to bottom"
      },
      image: { url: "", mode: "bg" },
      video: { url: "", poster: "" }
    },
    zones: {
      "container-content": { title: "Container Content" }
    },
    render: ({
      containerWidth,
      padding,
      shadow,
      backgroundType,
      backgroundColor,
      gradient,
      image,
      video,
      puck
    }) => {
      const { renderDropZone } = puck || {};

      if (!renderDropZone) {
        return React.createElement(
          "div",
          {
            style: {
              padding: "2rem",
              backgroundColor: "#f8f9fa",
              border: "2px dashed #dee2e6",
              borderRadius: "4px",
              textAlign: "center"
            }
          },
          "Drop zones not available"
        );
      }

      const paddingValue = padding ? `${padding}px` : "24px";
      const shadowStyle = shadow
        ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
        : "none";

      // Helper function to ensure color is a string
      function ensureColorString(color) {
        if (!color) {
          return null;
        }
        if (typeof color === "string") {
          return color;
        }
        if (
          typeof color === "object" &&
          typeof color.r !== "undefined" &&
          typeof color.g !== "undefined" &&
          typeof color.b !== "undefined"
        ) {
          const a = typeof color.a !== "undefined" ? color.a : 1;
          return `rgba(${Math.round(color.r)}, ${Math.round(
            color.g
          )}, ${Math.round(color.b)}, ${a})`;
        }
        return String(color);
      }

      // Determine image and video usage
      const shouldUseImage = backgroundType === "image";
      const shouldUseVideo = backgroundType === "video";
      const isInline = shouldUseImage && image && image.mode === "inline";
      const isBg = shouldUseImage && image && image.mode === "bg";
      const imageUrl = shouldUseImage && image && image.url;
      const videoUrl = shouldUseVideo && video && video.url;
      const videoPoster = shouldUseVideo && video && video.poster;

      // Build outer wrapper style (for background images/videos)
      const wrapperStyle = {
        marginBottom: "1rem",
        position: "relative",
        overflow: "hidden"
      };

      // Apply background image/video to wrapper if bg mode
      if (isBg && imageUrl) {
        wrapperStyle.backgroundImage = `url(${imageUrl})`;
        wrapperStyle.backgroundSize = "cover";
        wrapperStyle.backgroundPosition = "center";
        wrapperStyle.backgroundRepeat = "no-repeat";
      }

      // Build inner container style
      const containerStyle = {
        padding: paddingValue,
        borderRadius: "0.375rem",
        boxShadow: shadowStyle,
        minHeight: "100px",
        position: "relative"
      };

      // Apply background based on type (only if not bg image mode)
      if (!isBg) {
        if (backgroundType === "color" && backgroundColor) {
          containerStyle.backgroundColor = ensureColorString(backgroundColor);
        } else if (backgroundType === "gradient" && gradient?.enabled) {
          const gradientColor =
            ensureColorString(gradient.color) || "rgba(0, 0, 0, 0.5)";
          const gradientType = gradient.type || "linear";
          const direction = gradient.direction || "to bottom";

          if (gradientType === "linear") {
            containerStyle.background = `linear-gradient(${direction}, ${gradientColor}, transparent)`;
          } else {
            containerStyle.background = `radial-gradient(circle, ${gradientColor}, transparent)`;
          }
        } else {
          containerStyle.backgroundColor = "#fff";
        }
      }

      // Determine container class based on width setting
      const containerClass =
        containerWidth === "fluid" ? "container-fluid" : "container";

      // Render video background if needed
      const videoBackground =
        shouldUseVideo && videoUrl
          ? React.createElement(
              "video",
              {
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
                poster: videoPoster || null,
                style: {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  zIndex: 0
                }
              },
              React.createElement("source", {
                src: videoUrl,
                type: "video/mp4"
              })
            )
          : null;

      // Render inline image if needed
      const inlineImage =
        isInline && imageUrl
          ? React.createElement("img", {
              src: imageUrl,
              alt: "",
              style: {
                width: "100%",
                height: "auto",
                display: "block",
                marginBottom: paddingValue
              }
            })
          : null;

      // Full-Bleed Section: break out of the parent container to the full
      // viewport width and carry the background edge-to-edge, while keeping the
      // inner content constrained to a centered, readable max-width container.
      // NOTE: only color/gradient/image backgrounds bleed edge-to-edge here;
      // background "video" and inline-mode images render inside the centered
      // .container (not full-width) by design — use color/gradient/image bg for
      // a true edge-to-edge band.
      if (containerWidth === "fullbleed") {
        const fullBleedBg = {};
        if (backgroundType === "color" && backgroundColor) {
          fullBleedBg.backgroundColor = ensureColorString(backgroundColor);
        } else if (backgroundType === "gradient" && gradient?.enabled) {
          const gColor =
            ensureColorString(gradient.color) || "rgba(0, 0, 0, 0.5)";
          const gType = gradient.type || "linear";
          const gDir = gradient.direction || "to bottom";
          fullBleedBg.background =
            gType === "linear"
              ? `linear-gradient(${gDir}, ${gColor}, transparent)`
              : `radial-gradient(circle, ${gColor}, transparent)`;
        } else if (isBg && imageUrl) {
          // Sanitize before interpolating into an inline CSS url(): strip
          // characters that could break out of the url() and inject CSS.
          const safeBgUrl = String(imageUrl).replace(/["'()]/g, "");
          fullBleedBg.backgroundImage = `url("${safeBgUrl}")`;
          fullBleedBg.backgroundSize = "cover";
          fullBleedBg.backgroundPosition = "center";
          fullBleedBg.backgroundRepeat = "no-repeat";
        }

        // Robust full-bleed: negative side margins pull the block edges out to
        // the viewport without setting an explicit 100vw width (avoids the
        // horizontal-scrollbar overflow that width:100vw causes).
        // ANCESTOR CONTRACT: calc(50% - 50vw) only reaches the true viewport
        // edges when no ancestor clips overflow-x. If an ancestor sets
        // overflow:hidden the bleed is clipped; if one is narrower than the
        // viewport the bleed is still symmetric (50% is relative to this block's
        // own resolved width, which fills its container). Place fullbleed blocks
        // at the top level of the content tab, not nested inside a padded
        // .container/.col, for edge-to-edge results.
        const fullBleedWrapperStyle = {
          position: "relative",
          overflow: "hidden",
          marginBottom: "1rem",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          ...fullBleedBg
        };

        const fullBleedInnerStyle = {
          padding: paddingValue,
          position: "relative",
          zIndex: 1,
          boxShadow: shadowStyle
        };

        return React.createElement(
          "div",
          {
            className: "container-block-wrapper full-bleed-section",
            style: fullBleedWrapperStyle
          },
          videoBackground,
          React.createElement(
            "div",
            {
              className: "container",
              style: fullBleedInnerStyle
            },
            inlineImage,
            React.createElement(
              "div",
              {
                style: {
                  position: "relative",
                  zIndex: 1
                }
              },
              renderDropZone({ zone: "container-content" })
            )
          )
        );
      }

      return React.createElement(
        "div",
        {
          className: "container-block-wrapper",
          style: wrapperStyle
        },
        videoBackground,
        React.createElement(
          "div",
          {
            className: containerClass,
            style: containerStyle
          },
          inlineImage,
          React.createElement(
            "div",
            {
              style: {
                position: "relative",
                zIndex: 1
              }
            },
            renderDropZone({ zone: "container-content" })
          )
        )
      );
    }
  };
}
